// CreditNova Stage Tracker Component - Premium Midnight Luxe Design
// Visual progress indicator for the multi-step workflow
import { CheckCircle, FileBarChart, Calculator, FileText, Sparkles, Upload, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useWorkflow, WorkflowStep } from '@/contexts/WorkflowContext';

// ===========================================
// Icon Mapping
// The 'upload' key maps to the Upload icon for the unified upload page.
// ===========================================

const STEP_ICONS: Record<WorkflowStep, typeof FileBarChart> = {
  summary: Sparkles,
  upload: Upload,
  overrides: FileBarChart,
  metrics: Calculator,
  generating: Loader2,
  report: FileText,
};

// ===========================================
// StageTracker Component
// ===========================================

export function StageTracker() {
  const {
    currentStep,
    availableSteps,
    stepIndex,
    isStepComplete,
    goToStep,
    data,
  } = useWorkflow();

  // Once the report is generating or complete, previous steps are locked —
  // changes made there won't affect the in-flight/finished report.
  const isLocked = data.workflowState === 'generating' || data.workflowState === 'complete';

  // Don't show tracker on summary page (first step)
  if (currentStep === 'summary') {
    return null;
  }

  return (
    <div className="w-full glass border-b border-gold-500/10 py-8">
      <div className="max-w-6xl mx-auto px-8">
        <div className="relative">
          {/* Progress bar background */}
          <div className="absolute top-8 left-0 right-0 h-1 bg-border/50 rounded-full" />

          {/* Progress bar fill - golden gradient */}
          <div
            className="absolute top-8 left-0 h-1 rounded-full transition-all duration-700 ease-out-expo"
            style={{
              width: `${(stepIndex / (availableSteps.length - 1)) * 100}%`,
              background: 'linear-gradient(90deg, hsl(var(--gold-500)) 0%, hsl(var(--gold-400)) 100%)',
              boxShadow: '0 0 20px -5px hsl(var(--gold-500) / 0.5)',
            }}
          />

          {/* Stage nodes */}
          <div className="relative flex justify-between">
            {availableSteps.map((step, index) => {
              const isCompleted = isStepComplete(step.id) || index < stepIndex;
              const isCurrent = step.id === currentStep;
              const isUpcoming = index > stepIndex;
              // When locked (generating/complete), only the current step is interactive
              const isDisabled = isUpcoming || (isLocked && !isCurrent);
              const Icon = STEP_ICONS[step.id] || FileText;

              return (
                <button
                  key={step.id}
                  onClick={() => {
                    // Allow clicking on completed steps or current step
                    if (!isDisabled) {
                      goToStep(step.id);
                    }
                  }}
                  disabled={isDisabled}
                  className={cn(
                    'flex flex-col items-center transition-all duration-300',
                    !isDisabled ? 'cursor-pointer hover:scale-105' : 'cursor-not-allowed opacity-60'
                  )}
                >
                  {/* Stage circle */}
                  <div
                    className={cn(
                      'relative z-10 flex items-center justify-center rounded-xl transition-all duration-300',
                      isCurrent
                        ? 'w-16 h-16 shadow-glow-gold'
                        : isCompleted
                        ? 'w-14 h-14 shadow-premium'
                        : 'w-12 h-12 shadow-sm'
                    )}
                    style={{
                      background: isCurrent
                        ? 'linear-gradient(135deg, hsl(var(--gold-500)) 0%, hsl(var(--gold-400)) 100%)'
                        : isCompleted
                        ? 'linear-gradient(135deg, hsl(var(--emerald-500)) 0%, hsl(var(--emerald-400)) 100%)'
                        : 'hsl(var(--card))',
                      border: isCurrent
                        ? '2px solid hsl(var(--gold-300) / 0.5)'
                        : isCompleted
                        ? '2px solid hsl(var(--emerald-400) / 0.3)'
                        : '2px solid hsl(var(--border))',
                    }}
                  >
                    {isCompleted && !isCurrent ? (
                      <CheckCircle className="w-7 h-7 text-white" />
                    ) : (
                      <Icon
                        className={cn(
                          isCurrent ? 'w-7 h-7 text-primary-950' : 'w-5 h-5',
                          !isCurrent && !isCompleted && 'text-muted-foreground'
                        )}
                      />
                    )}

                    {/* Pulse ring for current step */}
                    {isCurrent && (
                      <div className="absolute inset-0 rounded-xl animate-pulse-glow" />
                    )}
                  </div>

                  {/* Stage label */}
                  <div className="mt-4 text-center">
                    <p
                      className={cn(
                        'font-medium transition-all',
                        isCurrent
                          ? 'text-sm text-gold-400 font-semibold'
                          : isCompleted
                          ? 'text-sm text-emerald-400'
                          : 'text-xs text-muted-foreground'
                      )}
                    >
                      {step.shortName}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
